import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useRBAC } from '../../../contexts/RBACContext';
import { permissionAPI } from '../../../services/rbacApi';
import PermissionGuard from '../../Common/PermissionGuard';
import CustomSelect from '../../Common/CustomSelect';
import TextInput from '../../Common/TextInput';
import ConfirmationModal from '../../Common/ConfirmationModal';
import PermissionForm from './PermissionForm';
import {
  CardGridSkeleton,
  DataViewLoadingContainer,
  TableSkeleton,
} from '../Common/DataViewLoading';
import {
  buildPermissionExportFilename,
  exportPermissionsPdf,
  triggerBlobDownload,
} from '../../../utils/permissionExport';

const toolbarBtnClass =
  'inline-flex items-center justify-center h-8 px-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

const toolbarPrimaryBtnClass =
  'inline-flex items-center justify-center h-8 px-3 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 rounded-md shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

const toolbarAccentBtnClass =
  'inline-flex items-center justify-center h-8 px-3 text-sm font-medium text-indigo-700 bg-white border border-indigo-200 rounded-md hover:bg-indigo-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

const filterSelectClass = 'min-w-0 w-full !rounded-sm';

const PermissionManagement = () => {
  const {
    permissions,
    loading,
    error,
    filters,
    pagination,
    fetchPermissions,
    createPermission,
    updatePermission,
    deletePermission,
    syncPermissions,
    setFilters,
    updatePagination
  } = useRBAC();

  const [showForm, setShowForm] = useState(false);
  const [editingPermission, setEditingPermission] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [view, setView] = useState('card'); // 'card', 'list'
  const [groupMode, setGroupMode] = useState('none'); // 'none', 'module', 'action'
  const [showError, setShowError] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncSummary, setSyncSummary] = useState(null);
  const [showSyncConfirm, setShowSyncConfirm] = useState(false);
  const [refreshCountdown, setRefreshCountdown] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ modules: [], actions: [] });
  const [exporting, setExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const [isMounted, setIsMounted] = useState(false);

  // Default filters for comparison
  const defaultFilters = {
    search: '',
    isActive: true,
    permissionModule: null,
    permissionAction: null,
  };

  // Reset filters once on mount
  useEffect(() => {
    setSearchInput('');
    setFilters(defaultFilters);
    updatePagination('permissions', { current: 1, limit: 20, total: 0, pages: 0 });
    setIsMounted(true);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      setSearchInput('');
      setFilters(defaultFilters);
      setIsMounted(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isMounted) return;
    fetchPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMounted,
    filters.search,
    filters.isActive,
    filters.permissionModule,
    filters.permissionAction,
    pagination.permissions.current,
    pagination.permissions.limit,
  ]);

  const loadFilterOptions = useCallback(async () => {
    try {
      const isActiveParam = filters.isActive !== undefined && filters.isActive !== null
        ? String(filters.isActive)
        : undefined;
      const response = await permissionAPI.getPermissionFilterOptions(
        isActiveParam !== undefined ? { isActive: isActiveParam } : {}
      );
      setFilterOptions({
        modules: response.data?.data?.modules || [],
        actions: response.data?.data?.actions || [],
      });
    } catch {
      setFilterOptions({ modules: [], actions: [] });
    }
  }, [filters.isActive]);

  useEffect(() => {
    if (isMounted) {
      loadFilterOptions();
    }
  }, [isMounted, loadFilterOptions]);

  useEffect(() => {
    if (!showExportMenu) return undefined;
    const onClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [showExportMenu]);

  // Auto-dismiss error after 5 seconds
  useEffect(() => {
    if (error) {
      setShowError(true);
      const timer = setTimeout(() => {
        setShowError(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (refreshCountdown === null) {
      return undefined;
    }

    if (refreshCountdown <= 0) {
      window.location.reload();
      return undefined;
    }

    const timer = setTimeout(() => {
      setRefreshCountdown((current) => (current === null ? null : current - 1));
    }, 1000);

    return () => clearTimeout(timer);
  }, [refreshCountdown]);

  // Get unique modules and actions for filters
  const { modules, actions } = useMemo(() => {
    const moduleSet = new Set();
    const actionSet = new Set();
    
    permissions.forEach(permission => {
      if (permission.module) moduleSet.add(permission.module);
      if (permission.action) actionSet.add(permission.action);
    });

    return {
      modules: Array.from(moduleSet).sort(),
      actions: Array.from(actionSet).sort()
    };
  }, [permissions]);

  // For grouping - we'll use all permissions since API already filtered
  const filteredPermissions = permissions;

  // Group permissions by module for module view
  const permissionsByModule = useMemo(() => {
    const grouped = {};
    filteredPermissions.forEach(permission => {
      const module = permission.module || 'Unassigned';
      if (!grouped[module]) {
        grouped[module] = [];
      }
      grouped[module].push(permission);
    });
    return grouped;
  }, [filteredPermissions]);

  // Group permissions by action for action view
  const permissionsByAction = useMemo(() => {
    const grouped = {};
    filteredPermissions.forEach(permission => {
      const action = permission.action || 'Unassigned';
      if (!grouped[action]) {
        grouped[action] = [];
      }
      grouped[action].push(permission);
    });
    return grouped;
  }, [filteredPermissions]);

  const handleCreatePermission = () => {
    setEditingPermission(null);
    setShowForm(true);
  };

  const handleEditPermission = (permission) => {
    setEditingPermission(permission);
    setShowForm(true);
  };

  const handleDeletePermission = async (permission) => {
    setIsDeleting(true);
    try {
      await deletePermission(permission._id);
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete permission:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFormSubmit = async (permissionData) => {
    try {
      if (editingPermission) {
        await updatePermission(editingPermission._id, permissionData);
      } else {
        await createPermission(permissionData);
      }
      setShowForm(false);
      setEditingPermission(null);
    } catch (error) {
      console.error('Failed to save permission:', error);
    }
  };

  const handleSyncPermissions = async () => {
    setIsSyncing(true);
    setSyncSummary(null);
    setShowSyncConfirm(false);

    try {
      const result = await syncPermissions();
      if (result.success) {
        setSyncSummary(result.data.summary || null);
        setRefreshCountdown(5);
      }
    } catch (error) {
      console.error('Failed to sync permissions:', error);
    } finally {
      setIsSyncing(false);
    }
  };



  // Pagination handlers
  const handlePageChange = (newPage) => {
    updatePagination('permissions', { current: newPage });
  };

  const handleLimitChange = (newLimit) => {
    updatePagination('permissions', { current: 1, limit: newLimit });
  };

  // Debounced search handler
  const handleSearch = (searchTerm) => {
    setSearchInput(searchTerm);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounce (500ms)
    searchTimeoutRef.current = setTimeout(() => {
      setFilters({ search: searchTerm });
      updatePagination('permissions', { current: 1 });
    }, 500);
  };

  const handleActiveFilter = (isActive) => {
    setFilters({ isActive: isActive });
    updatePagination('permissions', { current: 1 });
  };

  const handleModuleFilter = (moduleValue) => {
    setFilters({ permissionModule: moduleValue || null });
    updatePagination('permissions', { current: 1 });
  };

  const handleActionFilter = (actionValue) => {
    setFilters({ permissionAction: actionValue || null });
    updatePagination('permissions', { current: 1 });
  };

  const buildExportQueryParams = () => ({
    ...(filters.search ? { search: filters.search } : {}),
    ...(filters.permissionModule ? { module: filters.permissionModule } : {}),
    ...(filters.permissionAction ? { action: filters.permissionAction } : {}),
    ...(filters.isActive !== undefined && filters.isActive !== null ? { isActive: String(filters.isActive) } : {}),
  });

  const buildExportFiltersSummary = () => {
    const parts = [];
    if (filters.search) parts.push(`Search: ${filters.search}`);
    if (filters.permissionModule) parts.push(`Module: ${filters.permissionModule}`);
    if (filters.permissionAction) parts.push(`Action: ${filters.permissionAction}`);
    if (filters.isActive === true) parts.push('Status: Active');
    if (filters.isActive === false) parts.push('Status: Inactive');
    return parts.length ? `Filters: ${parts.join(' | ')}` : 'Filters: All permissions';
  };

  const handleExportCsv = async () => {
    setShowExportMenu(false);
    setExporting(true);
    try {
      const response = await permissionAPI.exportPermissions({
        ...buildExportQueryParams(),
        format: 'csv',
      });
      triggerBlobDownload(
        response.data,
        buildPermissionExportFilename({ module: filters.permissionModule || '', extension: 'csv' }),
        'text/csv;charset=utf-8'
      );
      toast.success('Permissions exported to CSV');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to export permissions');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setShowExportMenu(false);
    setExporting(true);
    try {
      const response = await permissionAPI.exportPermissions({
        ...buildExportQueryParams(),
        format: 'json',
      });
      const rows = response.data?.data?.permissions || [];
      if (!rows.length) {
        toast.error('No permissions match the current filters');
        return;
      }
      exportPermissionsPdf(rows, {
        title: 'System Permissions',
        filtersSummary: buildExportFiltersSummary(),
        module: filters.permissionModule || '',
      });
      toast.success('Permissions exported to PDF');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to export permissions');
    } finally {
      setExporting(false);
    }
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setFilters(defaultFilters);
    updatePagination('permissions', { current: 1 });
  };

  // Check if filters are modified from defaults
  const hasModifiedFilters = () =>
    filters.search !== defaultFilters.search
    || filters.isActive !== defaultFilters.isActive
    || filters.permissionModule !== defaultFilters.permissionModule
    || filters.permissionAction !== defaultFilters.permissionAction;

  const moduleFilterOptions = useMemo(() => [
    { value: '', label: 'All Modules' },
    ...filterOptions.modules.map((row) => ({
      value: row.module,
      label: `${row.module} (${row.count})`,
    })),
  ], [filterOptions.modules]);

  const actionFilterOptions = useMemo(() => [
    { value: '', label: 'All Actions' },
    ...filterOptions.actions.map((action) => ({ value: action, label: action })),
  ], [filterOptions.actions]);

  const getPermissionIcon = (action) => {
    const icons = {
      'CREATE': '➕',
      'READ': '👁️',
      'UPDATE': '✏️',
      'DELETE': '🗑️',
      'MANAGE': '⚙️',
      'VIEW': '👀',
      'EXECUTE': '⚡'
    };
    return icons[action] || '🔧';
  };

  const renderPermissionCard = (permission) => (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 group h-28 w-full">
      <div className="p-3 h-full flex flex-col">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <div className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-semibold flex-shrink-0 ${
              permission.action === 'CREATE' ? 'bg-green-100 text-green-600' :
              permission.action === 'READ' ? 'bg-blue-100 text-blue-600' :
              permission.action === 'UPDATE' ? 'bg-yellow-100 text-yellow-600' :
              permission.action === 'DELETE' ? 'bg-red-100 text-red-600' :
              permission.action === 'MANAGE' ? 'bg-purple-100 text-purple-600' :
              'bg-gray-100 text-gray-600'
            }`}>
              {getPermissionIcon(permission.action)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center space-x-1">
                <h3 className="text-sm font-semibold text-gray-900 truncate">
                  {permission.name}
                </h3>
                {!permission.isActive && (
                  <span className="inline-block w-2 h-2 bg-red-500 rounded-full flex-shrink-0" title="Disabled"></span>
                )}
              </div>
              <p className="text-xs text-gray-500 font-mono truncate">{permission.code}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
            <button
              onClick={() => handleEditPermission(permission)}
              className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
              title="Edit permission"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            
            <PermissionGuard permission="USERS:PERMISSIONS:MANAGE">
              <button
                onClick={() => setShowDeleteConfirm(permission)}
                className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                title="Delete permission"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </PermissionGuard>
          </div>
        </div>
        
        {permission.description && (
          <p className="text-xs text-gray-600 mb-2 line-clamp-2 leading-tight flex-1">
            {permission.description}
          </p>
        )}
        
        <div className="flex flex-wrap gap-1 mt-auto">
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
            {permission.module || 'N/A'}
          </span>
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-200">
            {permission.action || 'N/A'}
          </span>
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
            {permission.resource || 'N/A'}
          </span>
        </div>
      </div>
    </div>
  );

  const renderPermissionRow = (permission) => (
    <tr key={permission._id} className="hover:bg-gray-50 transition-colors">
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex items-center space-x-3">
          <div className={`w-8 h-8 rounded flex items-center justify-center text-sm font-semibold ${
            permission.action === 'CREATE' ? 'bg-green-100 text-green-600' :
            permission.action === 'READ' ? 'bg-blue-100 text-blue-600' :
            permission.action === 'UPDATE' ? 'bg-yellow-100 text-yellow-600' :
            permission.action === 'DELETE' ? 'bg-red-100 text-red-600' :
            permission.action === 'MANAGE' ? 'bg-purple-100 text-purple-600' :
            'bg-gray-100 text-gray-600'
          }`}>
            {getPermissionIcon(permission.action)}
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <p className="text-sm font-semibold text-gray-900">{permission.name}</p>
              {!permission.isActive && (
                <span className="inline-block w-2 h-2 bg-red-500 rounded-full" title="Disabled"></span>
              )}
            </div>
            <p className="text-xs text-gray-500 font-mono">{permission.code}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
          {permission.module || 'N/A'}
        </span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
          permission.action === 'CREATE' ? 'bg-green-100 text-green-800' :
          permission.action === 'READ' ? 'bg-blue-100 text-blue-800' :
          permission.action === 'UPDATE' ? 'bg-yellow-100 text-yellow-800' :
          permission.action === 'DELETE' ? 'bg-red-100 text-red-800' :
          permission.action === 'MANAGE' ? 'bg-purple-100 text-purple-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {permission.action || 'N/A'}
        </span>
      </td>
      <td className="px-4 py-3">
        <p className="text-sm text-gray-700 line-clamp-2">{permission.description || 'No description'}</p>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
          permission.isActive !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {permission.isActive !== false ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex items-center justify-end space-x-2">
          <button
            onClick={() => handleEditPermission(permission)}
            className="text-indigo-600 hover:text-indigo-900 p-1 hover:bg-indigo-50 rounded transition-all"
            title="Edit permission"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>

          <PermissionGuard permission="USERS:PERMISSIONS:MANAGE">
            <button
              onClick={() => setShowDeleteConfirm(permission)}
              className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-all"
              title="Delete permission"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </PermissionGuard>
        </div>
      </td>
    </tr>
  );

  const isPermissionsLoading = Boolean(loading.permissions);
  const showPermissionsSkeleton = isPermissionsLoading && filteredPermissions.length === 0;
  const showPermissionsOverlay = isPermissionsLoading && filteredPermissions.length > 0;

  return (
    <div>
      <div className="max-w-7xl mx-auto">
        {/* Error Display */}
        {error && showError && (
          <div className="mb-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded p-3">
            <div className="flex items-center">
              <svg className="h-4 w-4 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-medium text-red-800">{error}</p>
              <button
                onClick={() => setShowError(false)}
                className="ml-auto text-red-600 hover:text-red-500"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {syncSummary && (
          <div className="mb-4 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded p-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-emerald-800">Permission sync completed</p>
                <p className="text-sm text-emerald-700">
                  Created {syncSummary.created || 0}, updated {syncSummary.updated || 0}, deleted {syncSummary.deleted || 0}.
                </p>
              </div>
              <div className="text-xs text-emerald-700">
                Role links removed: {syncSummary.deletedRoleAssignments || 0} | Users cleaned: {syncSummary.cleanedUserDocuments || 0}
              </div>
            </div>
            {syncSummary.cacheWarning && (
              <p className="text-xs text-emerald-700 mt-2">
                {syncSummary.cacheWarning}
              </p>
            )}
          </div>
        )}

        {/* Main Content */}
        {showForm && (
          <PermissionForm
            permission={editingPermission}
            onSubmit={handleFormSubmit}
            onCancel={() => {
              setShowForm(false);
              setEditingPermission(null);
            }}
            modules={modules}
            actions={actions}
          />
        )}

        {!showForm && (
          <div className="bg-white shadow-sm border border-gray-200">
            {/* Filters and Search */}
            <div className="px-4 sm:px-6 lg:px-8 py-3 border-b border-gray-200 flex-shrink-0 space-y-3">
              {/* Row 1 — full-width search */}
              <div className="w-full min-w-0">
                <TextInput
                  variant="input"
                  type="text"
                  placeholder="Search permissions by name or code..."
                  value={searchInput}
                  onChange={(e) => handleSearch(e.target.value)}
                  icon={({ className }) => (
                    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  )}
                  className="w-full text-sm py-2 border border-gray-300 shadow-sm !rounded-sm"
                />
              </div>

              {/* Row 2 — filters */}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-2">
                <div className="min-w-0">
                  <p className="text-[11px] font-medium text-gray-500 mb-1">Status</p>
                  <CustomSelect
                    value={filters.isActive === true ? 'active' : filters.isActive === false ? 'inactive' : 'all'}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleActiveFilter(value === 'all' ? undefined : value === 'active');
                    }}
                    options={[
                      { value: 'all', label: 'All Status' },
                      { value: 'active', label: 'Active' },
                      { value: 'inactive', label: 'Inactive' },
                    ]}
                    placeholder=""
                    size="sm"
                    variant="default"
                    className={filterSelectClass}
                  />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-medium text-gray-500 mb-1">Module</p>
                  <CustomSelect
                    value={filters.permissionModule || ''}
                    onChange={(e) => handleModuleFilter(e.target.value)}
                    options={moduleFilterOptions}
                    placeholder=""
                    size="sm"
                    variant="default"
                    className={filterSelectClass}
                  />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-medium text-gray-500 mb-1">Action</p>
                  <CustomSelect
                    value={filters.permissionAction || ''}
                    onChange={(e) => handleActionFilter(e.target.value)}
                    options={actionFilterOptions}
                    placeholder=""
                    size="sm"
                    variant="default"
                    className={filterSelectClass}
                  />
                </div>
                <div className="min-w-0 flex flex-col justify-end">
                  <p className="text-[11px] font-medium text-gray-500 mb-1 sm:invisible">Export</p>
                  <div className="flex flex-wrap items-center gap-2">
                    {hasModifiedFilters() && (
                      <button
                        type="button"
                        onClick={handleResetFilters}
                        className={toolbarBtnClass}
                        title="Reset filters"
                      >
                        <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Reset
                      </button>
                    )}
                    <div className="relative" ref={exportMenuRef}>
                      <button
                        type="button"
                        onClick={() => setShowExportMenu((open) => !open)}
                        disabled={exporting || loading.permissions}
                        className={toolbarBtnClass}
                      >
                        <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {exporting ? 'Exporting…' : 'Export'}
                        <svg className="w-3 h-3 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {showExportMenu && (
                        <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-30 py-1">
                          <button
                            type="button"
                            onClick={handleExportCsv}
                            disabled={exporting}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                          >
                            Export CSV / Excel
                          </button>
                          <button
                            type="button"
                            onClick={handleExportPdf}
                            disabled={exporting}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                          >
                            Export PDF
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 3 — view options + admin actions */}
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between pt-1 border-t border-gray-100">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] font-medium text-gray-500 mr-1">View</span>
                  <div className="flex bg-gray-100 rounded-md border border-gray-200 p-0.5 h-8">
                    <button
                      type="button"
                      onClick={() => setView('card')}
                      className={`inline-flex items-center px-2.5 h-full text-xs font-medium rounded transition-all ${
                        view === 'card'
                          ? 'bg-white text-indigo-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                      }`}
                      title="Card view"
                    >
                      <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14-7H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2z" />
                      </svg>
                      Cards
                    </button>
                    <button
                      type="button"
                      onClick={() => setView('list')}
                      className={`inline-flex items-center px-2.5 h-full text-xs font-medium rounded transition-all ${
                        view === 'list'
                          ? 'bg-white text-indigo-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                      }`}
                      title="List view"
                    >
                      <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                      List
                    </button>
                  </div>

                  {view === 'card' && (
                    <>
                      <span className="text-[11px] font-medium text-gray-500 mx-1 hidden sm:inline">Group</span>
                      <div className="flex bg-gray-100 rounded-md border border-gray-200 p-0.5 h-8">
                        <button
                          type="button"
                          onClick={() => setGroupMode('none')}
                          className={`px-2.5 h-full text-xs font-medium rounded transition-all ${
                            groupMode === 'none'
                              ? 'bg-white text-indigo-700 shadow-sm'
                              : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                          }`}
                          title="No grouping"
                        >
                          All
                        </button>
                        <button
                          type="button"
                          onClick={() => setGroupMode('module')}
                          className={`px-2.5 h-full text-xs font-medium rounded transition-all ${
                            groupMode === 'module'
                              ? 'bg-white text-indigo-700 shadow-sm'
                              : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                          }`}
                          title="Group by module"
                        >
                          Module
                        </button>
                        <button
                          type="button"
                          onClick={() => setGroupMode('action')}
                          className={`px-2.5 h-full text-xs font-medium rounded transition-all ${
                            groupMode === 'action'
                              ? 'bg-white text-indigo-700 shadow-sm'
                              : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                          }`}
                          title="Group by action"
                        >
                          Action
                        </button>
                      </div>
                    </>
                  )}
                </div>

                <PermissionGuard permission="USERS:PERMISSIONS:MANAGE">
                  <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                    <button
                      type="button"
                      onClick={() => setShowSyncConfirm(true)}
                      disabled={isSyncing || loading.permissions}
                      className={toolbarAccentBtnClass}
                    >
                      {isSyncing ? (
                        <svg className="animate-spin w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      ) : (
                        <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      )}
                      {isSyncing ? 'Syncing…' : 'Sync'}
                    </button>
                    <button
                      type="button"
                      onClick={handleCreatePermission}
                      className={toolbarPrimaryBtnClass}
                    >
                      <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Permission
                    </button>
                  </div>
                </PermissionGuard>
              </div>
            </div>

        {/* Modern Pagination Section */}
        {pagination?.permissions && (
          <div className="bg-gradient-to-r from-gray-100 to-gray-100 px-2 sm:px-4 lg:px-6 py-2 border-b border-gray-200 flex-shrink-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              
              {/* Results Info */}
              <div className="flex items-center gap-2">
                {/* Results Summary */}
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div className="text-sm flex items-center gap-2">
                    <div>
                      <span className="font-semibold text-gray-900">
                        {((pagination.permissions.current - 1) * pagination.permissions.limit) + 1}-{Math.min(pagination.permissions.current * pagination.permissions.limit, pagination.permissions.total)}
                      </span>
                      <span className="text-gray-500 mx-1">of</span>
                      <span className="font-semibold text-gray-900">{pagination.permissions.total}</span>
                      <span className="text-gray-500 ml-1">permissions</span>
                    </div>
                    {loading.permissions && permissions.length > 0 && (
                      <div className="flex items-center">
                        <svg className="animate-spin h-3 w-3 text-indigo-500" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Navigation Controls */}
              <div className="flex items-center gap-3">
                {/* Items per page */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 font-medium">Show:</span>
                  <CustomSelect
                    id="limit"
                    value={pagination.permissions.limit}
                    onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                    disabled={loading.permissions}
                    options={[
                      { value: 10, label: '10' },
                      { value: 20, label: '20' },
                      { value: 50, label: '50' },
                      { value: 100, label: '100' }
                    ]}
                    size="sm"
                    variant="minimal"
                    className="min-w-[70px]"
                  />
                </div>

                {/* Navigation Buttons */}
                <div className="flex items-center gap-1">
                  {/* First Page */}
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={pagination.permissions.current <= 1 || loading.permissions}
                    className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    title="First page"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                    </svg>
                  </button>

                  {/* Previous Page */}
                  <button
                    onClick={() => handlePageChange(Math.max(1, pagination.permissions.current - 1))}
                    disabled={pagination.permissions.current <= 1 || loading.permissions}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    <span className="hidden sm:inline">Previous</span>
                  </button>

                  {/* Page Numbers */}
                  <div className="hidden md:flex items-center gap-1">
                    {Array.from({ length: Math.min(5, pagination.permissions.pages) }, (_, i) => {
                      let pageNum;
                      if (pagination.permissions.pages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.permissions.current <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.permissions.current >= pagination.permissions.pages - 2) {
                        pageNum = pagination.permissions.pages - 4 + i;
                      } else {
                        pageNum = pagination.permissions.current - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          disabled={loading.permissions}
                          className={`min-w-[32px] h-8 rounded text-sm font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                            pageNum === pagination.permissions.current
                              ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-md shadow-indigo-500/20'
                              : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  {/* Next Page */}
                  <button
                    onClick={() => handlePageChange(Math.min(pagination.permissions.pages, pagination.permissions.current + 1))}
                    disabled={pagination.permissions.current >= pagination.permissions.pages || loading.permissions}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm"
                  >
                    <span className="hidden sm:inline">Next</span>
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  {/* Last Page */}
                  <button
                    onClick={() => handlePageChange(pagination.permissions.pages)}
                    disabled={pagination.permissions.current >= pagination.permissions.pages || loading.permissions}
                    className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    title="Last page"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Mobile Page Numbers */}
            <div className="flex md:hidden items-center justify-center gap-1 mt-3 pt-3 border-t border-gray-200">
              {Array.from({ length: Math.min(7, pagination.permissions.pages) }, (_, i) => {
                let pageNum;
                if (pagination.permissions.pages <= 7) {
                  pageNum = i + 1;
                } else if (pagination.permissions.current <= 4) {
                  pageNum = i + 1;
                } else if (pagination.permissions.current >= pagination.permissions.pages - 3) {
                  pageNum = pagination.permissions.pages - 6 + i;
                } else {
                  pageNum = pagination.permissions.current - 3 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    disabled={loading.permissions}
                    className={`min-w-[32px] h-8 rounded text-sm font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                      pageNum === pagination.permissions.current
                        ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-md shadow-indigo-500/20'
                        : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-2">
          {showPermissionsSkeleton ? (
            view === 'list' ? (
              <TableSkeleton
                columns={6}
                rows={Math.min(pagination?.permissions?.limit || 20, 10)}
                columnWidths={['w-28', 'w-16', 'w-16', 'w-40', 'w-14', 'w-16']}
              />
            ) : (
              <CardGridSkeleton count={8} />
            )
          ) : filteredPermissions.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No permissions found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {filters.search || filters.isActive === false || filters.permissionModule || filters.permissionAction
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Get started by creating your first permission.'
                }
              </p>
              {(!filters.search && filters.isActive !== false && !filters.permissionModule && !filters.permissionAction) && (
                <div className="mt-6">
                  <PermissionGuard permission="USERS:PERMISSIONS:MANAGE">
                    <button
                      onClick={handleCreatePermission}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Create Permission
                    </button>
                  </PermissionGuard>
                </div>
              )}
            </div>
          ) : (
            <DataViewLoadingContainer
              loading={showPermissionsOverlay}
              message={view === 'list' ? 'Loading permissions…' : 'Loading…'}
            >
              {/* Card View - No Grouping */}
              {view === 'card' && groupMode === 'none' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                  {filteredPermissions.map(permission => (
                    <div key={permission._id}>
                      {renderPermissionCard(permission)}
                    </div>
                  ))}
                </div>
              )}

              {/* Card View - Grouped by Module */}
              {view === 'card' && groupMode === 'module' && (
                <div className="space-y-4">
                  {Object.entries(permissionsByModule).map(([module, modulePermissions]) => (
                    <div key={module} className="bg-gray-50 shadow rounded-lg">
                      <div className="px-4 py-3 border-b border-gray-200">
                        <h3 className="text-base font-medium text-gray-900">
                          {module} ({modulePermissions.length})
                        </h3>
                      </div>
                      <div className="p-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                          {modulePermissions.map(permission => (
                            <div key={permission._id}>
                              {renderPermissionCard(permission)}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Card View - Grouped by Action */}
              {view === 'card' && groupMode === 'action' && (
                <div className="space-y-4">
                  {Object.entries(permissionsByAction).map(([action, actionPermissions]) => (
                    <div key={action} className="bg-gray-50 shadow rounded-lg">
                      <div className="px-4 py-3 border-b border-gray-200">
                        <h3 className="text-base font-medium text-gray-900 flex items-center">
                          <span className="mr-2">{getPermissionIcon(action)}</span>
                          {action} ({actionPermissions.length})
                        </h3>
                      </div>
                      <div className="p-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                          {actionPermissions.map(permission => (
                            <div key={permission._id}>
                              {renderPermissionCard(permission)}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* List View - Table */}
              {view === 'list' && (
                <div className="overflow-x-auto max-h-[70vh]">
                  <table className="min-w-max w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Permission
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Module
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Action
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredPermissions.map(renderPermissionRow)}
                    </tbody>
                  </table>
                </div>
              )}
            </DataViewLoadingContainer>
          )}
        </div>
          </div>
        )}

        {/* Bottom Pagination */}
        {pagination?.permissions && (
          <div className="bg-gradient-to-r from-gray-100 to-gray-100 px-2 sm:px-4 lg:px-6 py-2 border-t border-gray-200 flex-shrink-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="text-sm">
                  <span className="font-semibold text-gray-900">{((pagination.permissions.current - 1) * pagination.permissions.limit) + 1}-{Math.min(pagination.permissions.current * pagination.permissions.limit, pagination.permissions.total)}</span>
                  <span className="text-gray-500 mx-1">of</span>
                  <span className="font-semibold text-gray-900">{pagination.permissions.total}</span>
                  <span className="text-gray-500 ml-1">permissions</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => handlePageChange(1)} disabled={pagination.permissions.current <= 1 || loading.permissions} className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all" title="First page">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" /></svg>
                </button>
                <button onClick={() => handlePageChange(Math.max(1, pagination.permissions.current - 1))} disabled={pagination.permissions.current <= 1 || loading.permissions} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  <span className="hidden sm:inline">Previous</span>
                </button>
                <div className="hidden md:flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.permissions.pages) }, (_, i) => {
                    let pageNum;
                    if (pagination.permissions.pages <= 5) pageNum = i + 1;
                    else if (pagination.permissions.current <= 3) pageNum = i + 1;
                    else if (pagination.permissions.current >= pagination.permissions.pages - 2) pageNum = pagination.permissions.pages - 4 + i;
                    else pageNum = pagination.permissions.current - 2 + i;
                    return (
                      <button key={pageNum} onClick={() => handlePageChange(pageNum)} disabled={loading.permissions} className={`min-w-[32px] h-8 rounded text-sm font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed ${pageNum === pagination.permissions.current ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-md shadow-indigo-500/20' : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'}`}>{pageNum}</button>
                    );
                  })}
                </div>
                <button onClick={() => handlePageChange(Math.min(pagination.permissions.pages, pagination.permissions.current + 1))} disabled={pagination.permissions.current >= pagination.permissions.pages || loading.permissions} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all font-medium text-sm">
                  <span className="hidden sm:inline">Next</span>
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
                <button onClick={() => handlePageChange(pagination.permissions.pages)} disabled={pagination.permissions.current >= pagination.permissions.pages || loading.permissions} className="p-1.5 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all" title="Last page">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg>
                </button>
              </div>
            </div>
          </div>
        )}

      <ConfirmationModal
        isOpen={showSyncConfirm}
        onClose={() => setShowSyncConfirm(false)}
        onCancel={() => setShowSyncConfirm(false)}
        onConfirm={handleSyncPermissions}
        title="Sync Permissions"
        message="Sync permissions with the hardcoded registry? This will create missing permissions, update changed metadata, and delete obsolete permissions from the permission catalog, affected role assignments, and affected users."
        confirmText="Sync Permissions"
        cancelText="Cancel"
        type="warning"
        loading={isSyncing}
      />

      {refreshCountdown !== null && (
        <div className="fixed inset-0 z-[70] bg-white/30 backdrop-blur-md flex items-center justify-center p-4">
          <div className="w-full max-w-lg rounded-2xl border border-indigo-200 bg-white/95 shadow-2xl p-6 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-indigo-100">
              <svg className="h-7 w-7 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Permissions synced</h3>
            <p className="mt-2 text-sm text-gray-600">
              Reloading to refresh the current user's permissions.
            </p>
            <p className="mt-4 text-base font-medium text-indigo-700">
              Refreshing in {refreshCountdown} second{refreshCountdown === 1 ? '' : 's'}...
            </p>
            {syncSummary && (
              <p className="mt-2 text-xs text-gray-500">
                Created {syncSummary.created || 0}, updated {syncSummary.updated || 0}, deleted {syncSummary.deleted || 0}.
              </p>
            )}
          </div>
        </div>
      )}

    {/* Delete Confirmation Modal */}
    {showDeleteConfirm && (
        <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded shadow-2xl border border-gray-100 w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded mb-4">
                <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>

              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Permission</h3>
                <p className="text-sm text-gray-600">
                  Are you sure you want to delete <span className="font-medium text-gray-900">"{showDeleteConfirm.name}"</span>?
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  This action cannot be undone and may affect roles that use this permission.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 font-medium rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeletePermission(showDeleteConfirm)}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isDeleting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Deleting...</span>
                    </>
                  ) : (
                    'Delete Permission'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default PermissionManagement;